import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, Pause, Volume2, VolumeX, X, ChevronRight, Rocket } from "lucide-react";
import { api } from "@/lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const absUrl = (path) => (path?.startsWith("http") ? path : `${BACKEND_URL}${path}`);

// Stable per-tab session id so the backend can group events by visitor.
const TOUR_SESSION_KEY = "nexus_tour_session";
function getTourSessionId() {
  try {
    let id = sessionStorage.getItem(TOUR_SESSION_KEY);
    if (!id) {
      id = `t_${Math.random().toString(36).slice(2, 10)}_${Date.now().toString(36)}`;
      sessionStorage.setItem(TOUR_SESSION_KEY, id);
    }
    return id;
  } catch {
    return `t_${Date.now()}`;
  }
}
function fireTourEvent(event, extra = {}) {
  api.post("/tour/event", { event, session_id: getTourSessionId(), ...extra }).catch(() => {});
}

/**
 * TourPlayer — narrated product walkthrough.
 *
 * Two modes:
 *   <TourPlayer variant="inline" />        → muted autoplay preview (always visible)
 *   <TourPlayer variant="modal" onClose /> → full-screen dialog with controls
 *
 * Optional `onSignup` callback (modal only) is invoked when the viewer taps the
 * final-slide "Build my empire" CTA. Use this to open your signup modal.
 */
export default function TourPlayer({ variant = "modal", onClose, onSignup, initialChapter = 0 }) {
  const [chapters, setChapters] = useState([]);
  const [chapterIdx, setChapterIdx] = useState(initialChapter);
  const [playing, setPlaying] = useState(variant === "inline");
  const [muted, setMuted] = useState(variant === "inline");
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [loadError, setLoadError] = useState(false);
  const audioRef = useRef(null);
  const startedRef = useRef(false);

  // Load manifest once.
  useEffect(() => {
    let alive = true;
    api.get("/tour/manifest").then((r) => {
      if (!alive) return;
      setChapters(r.data.chapters || []);
    }).catch(() => alive && setLoadError(true));
    return () => { alive = false; };
  }, []);

  const chapter = chapters[chapterIdx];

  // Fire `start` once per session when the modal player opens.
  useEffect(() => {
    if (variant !== "modal" || !chapter || startedRef.current) return;
    startedRef.current = true;
    fireTourEvent("start", { chapter_id: chapter.id });
  }, [variant, chapter]);

  // (Re)bind audio whenever chapter changes.
  useEffect(() => {
    const a = audioRef.current;
    if (!a || !chapter) return;
    a.muted = muted;
    a.currentTime = 0;
    setTime(0);
    if (playing) a.play().catch(() => setPlaying(false));
  }, [chapterIdx, chapter?.id]);

  // Sync mute state.
  useEffect(() => {
    if (audioRef.current) audioRef.current.muted = muted;
  }, [muted]);

  const currentSlideIdx = useMemo(() => {
    if (!chapter) return 0;
    let idx = 0;
    for (let i = 0; i < chapter.slides.length; i++) {
      if (chapter.slides[i].at <= time) idx = i;
    }
    return idx;
  }, [time, chapter]);
  const currentSlide = chapter?.slides[currentSlideIdx];
  const progress = duration > 0 ? Math.min(100, (time / duration) * 100) : 0;
  const isFinalSlide = chapter
    ? chapterIdx === chapters.length - 1 && currentSlideIdx === chapter.slides.length - 1
    : false;

  const togglePlay = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) { a.pause(); setPlaying(false); }
    else { a.play().then(() => setPlaying(true)).catch(() => setPlaying(false)); }
  };

  const handleEnded = () => {
    if (variant === "modal" && chapter) {
      fireTourEvent("chapter_complete", { chapter_id: chapter.id });
    }
    if (chapterIdx < chapters.length - 1) {
      setChapterIdx(chapterIdx + 1);
      setPlaying(true);
    } else {
      setPlaying(false);
      if (variant === "modal") fireTourEvent("complete", { chapter_id: chapter?.id });
    }
  };

  const handleSignup = () => {
    fireTourEvent("cta_signup", { chapter_id: chapter?.id });
    if (onSignup) onSignup();
    else if (onClose) onClose();
  };

  const handleSkip = () => {
    fireTourEvent("skip", { chapter_id: chapter?.id, slide_idx: currentSlideIdx });
    if (onClose) onClose();
  };

  if (loadError) return null;
  if (!chapter) {
    return variant === "modal" ? (
      <div className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center text-white">Loading tour…</div>
    ) : null;
  }

  // ---- Slide canvas (shared between variants) ----
  const slideCanvas = (
    <div className="relative w-full h-full overflow-hidden rounded-2xl bg-gradient-to-br from-[#0A0B1A] via-[#10122A] to-[#1A0F2A]">
      {/* Animated glow */}
      <div
        className="absolute inset-0 opacity-60 transition-all duration-700"
        style={{
          background: `radial-gradient(circle at 30% 30%, ${currentSlide.accent}55, transparent 50%), radial-gradient(circle at 70% 70%, #1877F244, transparent 50%)`,
        }}
      />
      <div className="absolute inset-0 grain opacity-30 pointer-events-none" />
      <AnimatePresence mode="wait">
        <motion.div
          key={`${chapter.id}-${currentSlideIdx}`}
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -16 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          className="absolute inset-0 flex flex-col justify-center px-6 sm:px-10"
        >
          <div className="text-[10px] sm:text-xs uppercase tracking-[0.3em] mb-3" style={{ color: currentSlide.accent }}>
            {currentSlide.kicker}
          </div>
          <div className="font-display font-black tracking-tighter text-3xl sm:text-5xl text-white leading-[0.95]">
            {currentSlide.title}
          </div>
          <div className="mt-3 sm:mt-4 text-sm sm:text-base text-white/75 max-w-md leading-relaxed">
            {currentSlide.body}
          </div>
          {/* Final-slide CTA (modal-only) — appears on last slide of last chapter. */}
          {variant === "modal" && isFinalSlide && (
            <motion.button
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.4 }}
              onClick={handleSignup}
              data-testid="tour-cta-signup"
              className="mt-6 self-start inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white text-black font-bold text-sm shadow-2xl hover:scale-[1.03] transition"
            >
              <Rocket size={16} /> Build my empire — Sign up
            </motion.button>
          )}
        </motion.div>
      </AnimatePresence>
      {/* Footer dots */}
      <div className="absolute bottom-4 left-6 right-6 flex items-center gap-1.5">
        {chapter.slides.map((s, i) => (
          <div
            key={`${chapter.id}-dot-${i}`}
            className={`h-1 rounded-full transition-all duration-300 ${i === currentSlideIdx ? "flex-[2]" : "flex-1 opacity-40"}`}
            style={{ background: i <= currentSlideIdx ? currentSlide.accent : "rgba(255,255,255,0.4)" }}
          />
        ))}
      </div>
    </div>
  );

  // ---- Audio element (shared) ----
  const audioEl = (
    <audio
      ref={audioRef}
      src={absUrl(chapter.audio_url)}
      onTimeUpdate={(e) => setTime(e.currentTarget.currentTime)}
      onLoadedMetadata={(e) => setDuration(e.currentTarget.duration)}
      onEnded={handleEnded}
      onPlay={() => setPlaying(true)}
      onPause={() => setPlaying(false)}
      autoPlay={variant === "inline"}
      muted={variant === "inline"}
      loop={variant === "inline"}
    />
  );

  if (variant === "inline") {
    return (
      <div
        data-testid="tour-inline"
        className="relative w-full aspect-video rounded-2xl overflow-hidden cursor-pointer group"
        onClick={() => onClose && onClose("open")}
      >
        {slideCanvas}
        {audioEl}
        {/* Hover-only "Watch tour" ribbon */}
        <div className="absolute inset-0 flex items-end justify-center pb-12 pointer-events-none">
          <div className="glass px-4 py-2 rounded-full text-xs text-white flex items-center gap-2 opacity-90 group-hover:opacity-100 transition">
            <Play size={12} fill="currentColor" /> Watch the 2-min tour
          </div>
        </div>
      </div>
    );
  }

  // ---- Modal variant ----
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={handleSkip}
      data-testid="tour-modal"
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        transition={{ duration: 0.3 }}
        className="relative w-full max-w-4xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={handleSkip}
          data-testid="tour-close"
          className="absolute -top-3 -right-3 z-10 w-10 h-10 rounded-full bg-white text-black flex items-center justify-center shadow-2xl hover:scale-105 transition"
        >
          <X size={18} />
        </button>

        {/* Chapter tabs */}
        <div className="flex gap-2 mb-3">
          {chapters.map((c, i) => (
            <button
              key={c.id}
              data-testid={`tour-chapter-${c.id}`}
              onClick={() => { setChapterIdx(i); setPlaying(true); }}
              className={`px-4 py-2 rounded-full text-xs font-bold uppercase tracking-[0.2em] transition ${
                i === chapterIdx
                  ? "bg-white text-black"
                  : "bg-white/10 text-white/70 hover:bg-white/20"
              }`}
            >
              {c.title}
            </button>
          ))}
        </div>

        {/* Canvas */}
        <div className="aspect-video">
          {slideCanvas}
          {audioEl}
        </div>

        {/* Controls */}
        <div className="mt-4 flex items-center gap-4">
          <button
            onClick={togglePlay}
            data-testid="tour-toggle-play"
            className="w-12 h-12 rounded-full bg-white text-black flex items-center justify-center hover:scale-105 transition"
          >
            {playing ? <Pause size={18} fill="currentColor" /> : <Play size={18} fill="currentColor" />}
          </button>
          <button
            onClick={() => setMuted((m) => !m)}
            data-testid="tour-toggle-mute"
            className="w-10 h-10 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20 transition"
          >
            {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
          </button>
          <div className="flex-1 h-1.5 rounded-full bg-white/15 overflow-hidden">
            <div
              className="h-full transition-all"
              style={{ width: `${progress}%`, background: currentSlide.accent }}
            />
          </div>
          <div className="text-xs text-white/60 tabular-nums w-16 text-right">
            {fmt(time)} / {fmt(duration)}
          </div>
          {chapterIdx < chapters.length - 1 && (
            <button
              onClick={() => { setChapterIdx(chapterIdx + 1); setPlaying(true); }}
              data-testid="tour-next-chapter"
              className="text-xs text-white/80 hover:text-white flex items-center gap-1 px-3 py-2 rounded-full bg-white/10 hover:bg-white/20 transition"
            >
              Next <ChevronRight size={14} />
            </button>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}

function fmt(s) {
  if (!Number.isFinite(s)) return "0:00";
  const m = Math.floor(s / 60);
  const r = Math.floor(s % 60).toString().padStart(2, "0");
  return `${m}:${r}`;
}
